<template>
  <b-card
    ref="popupCard"
    :title="title"
    :sub-title="subtitle"
    :style="{'--font-family': fontFamily, 'font-family': fontFamily}"
    data-cy="popup-content"
    class="popup-content"
  >
    <hr v-if="title || subtitle">
    <p
      v-if="showTotal"
      class="total"
    >
      {{ totalLabel }}{{ totalString }}
      <span v-if="numProjects && showProjectCount">
        ({{ numProjects }}&nbsp;{{ projectString }})
      </span>
    </p>

    <GraphTable
      v-if="showChart"
      :id="graphId"
      :items="graphItems"
    />

    <div
      v-if="showCategories"
      class="mt-3"
    >
      <CategoryItem
        v-for="(item, index) in categoryItems"
        :key="index"
        :item="item"
        :node="node"
      />
    </div>
  </b-card>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import CategoryItem from '@/components/public/Popup/CategoryItem.vue'
import GraphTable from '@/components/public/GraphTable/GraphTable.vue'

import { PopupDetails, MapPopupDetails } from '@/types'

const components = {
  CategoryItem,
  GraphTable,
}
const props = {
  popupDetails: {
    type: Object as PropType<PopupDetails | MapPopupDetails | undefined>,
  },
  showChart: {
    type: Boolean,
    default: true,
  },
  graphId: {
    type: String,
    default: 'popupGraph',
  },
  titleProp: {
    type: String,
    default: null,
  },
}

@Component({ components })
export default class PopupContent extends Vue.extend({ props }) {
  resizeObserver: ResizeObserver|null = null
  mounted () {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.popupCard)
  }

  beforeDestroy () {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  handleResize () {
    this.$nextTick(() => {
      this.$emit('size-change', {
        clientWidth: this.popupCard.clientWidth,
        clientHeight: this.popupCard.clientHeight,
        offsetWidth: this.popupCard.offsetWidth,
        offsetHeight: this.popupCard.offsetHeight,
      })
    })
  }

  get popupCard () {
    return this.$refs.popupCard as HTMLElement
  }

  get hidePiechartOnZeroNegative () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.hidePiechartOnZeroNegative
  }

  get showProjectCount () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.showProjectCount
  }

  get projectPlural () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.projectPlural
  }

  get projectSingular () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.projectSingular
  }

  get formatValueByColumn () {
    return this.$typedStore.visualisationData.formatValueByColumn
  }

  get node () {
    return this.popupDetails?.node
  }

  get graphItems () {
    return this.$typedStore.visualisationApp.piechartItems
      .map((item) => {
        const title = this.$typedStore.visualisationApp.name(item.column)
        const value = this.node ? Number(this.node.data[item.column]) : 0
        return {
          title: title,
          value,
          formatted: this.formatValueByColumn(item.column, value),
          color: item.color,
        }
      })
      .filter((item) => {
        if (this.hidePiechartOnZeroNegative) return item.value > 0
        else return true
      })
  }

  get categoryItems () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.popupItems
  }

  get fontFamily () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.storySettings.sidebarBot.fontFamily
  }

  get isLeafLevel () {
    // default to true to handle nodes from map view as they may not have a path but we want to display leaf level data
    return (this.node?.path && this.node.path.level === this.$typedStore.activeVisualisation.leafLevel) ?? true
  }

  get levelField () {
    if (this.node?.path) {
      return this.$typedStore.activeVisualisation.visualisationStore.app.levels.fieldForLevel(this.node.path.level)
    }
    return undefined
  }

  get title () {
    if (this.titleProp !== null) {
      return this.titleProp
    } else {
      let titleField
      if (this.isLeafLevel) {
        titleField = this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.popupTitleField
      } else {
        titleField = this.levelField?.column
      }
      return titleField && this.node?.data ? this.node.data[titleField] : ''
    }
  }

  get subtitle () {
    if (this.isLeafLevel) {
      const subtitleField = this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.popupSubtitleField
      return subtitleField && this.node?.data ? this.node.data[subtitleField] : ''
    } else {
      return this.levelField?.name ?? ''
    }
  }

  get showTotal () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.showPopupTotal ?? true
  }

  get totalLabel () {
    const totalStringLabel = this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.totalStringLabel
    return totalStringLabel ? `${totalStringLabel}: ` : ''
  }

  get totalString () {
    if (this.node) return this.node.totalString
  }

  get numProjects () {
    const mapPopupDetails = this.popupDetails as MapPopupDetails
    if (mapPopupDetails) {
      return mapPopupDetails.projectCount
    }

    return 0
  }

  get projectString () {
    return this.numProjects > 1 ? this.projectPlural : this.projectSingular
  }

  get showCategories () {
    return this.node !== undefined
  }
}

</script>

<style>
/* NB Unscoped */

.popup-content .card-title, .popup-content .card-subtitle {
  font-family: var(--font-family);
  text-align: center;
}

.popup-content .card-title {
  /* bootstrap black */
  color: #212529;
}

.popup-content.card {
  box-shadow:0px 0px 10px 2px rgba(189,189,189,0.8);
}

.popup-content hr {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>
